import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { zusToast } from '@zep/toast';
import { CenterLayout } from '@/layout/getCenterLayout';
import { LoadingSpinner } from '@/shared/components';
import { trackCodeLoginEvent } from '@/shared/features/code-login/utils';
import { Button } from '@/ui_v3';
import { CodeKeyIcon, QrScanIcon } from './components/CodeLoginIcons';
import { CodeVerifyForm } from './components/CodeVerifyForm';
import { QrScanner } from './components/QrScanner';
import { useCodeLogin } from './hooks/useCodeLogin';
type Props = {
  /** gSSP 에서 파싱된 진입 코드(유효한 경우만). 자동 검증 트리거용. */
  initialCode: string;
  /** code 파라미터 원문. 잘못된 코드여도 입력창에 그대로 남겨 보여주기 위해 사용. */
  rawCode?: string;
  /** code 파라미터는 있었지만 형식이 잘못돼 파싱에 실패한 경우(딥링크 폴백 안내용). */
  hadInvalidCode?: boolean;
};

/** 진입 코드 자동 검증 중 표시하는 전체 화면 로딩. 폼이 깜빡이는 것을 막는다. */
function FullScreenLoading({
  label
}: {
  label: string;
}) {
  return <CenterLayout data-sentry-element="CenterLayout" data-sentry-component="FullScreenLoading" data-sentry-source-file="CodeLoginPageView.tsx">
      <div className="flex flex-col items-center gap-md">
        <LoadingSpinner className="size-8 text-violet-500" data-sentry-element="LoadingSpinner" data-sentry-source-file="CodeLoginPageView.tsx" />
        <p className="text-body-md-medium text-assistive">{label}</p>
      </div>
    </CenterLayout>;
}
export function CodeLoginPageView({
  initialCode,
  rawCode,
  hadInvalidCode
}: Props) {
  const {
    t
  } = useTranslation('classroom');
  const {
    handleVerify,
    handleScan,
    resetScanSession,
    isPending,
    emailLoginUrl
  } = useCodeLogin();

  // 딥링크로 유효 코드가 들어오면 마운트 즉시 검증을 시작하고, 끝날 때까지 폼 대신 로딩을 보여준다.
  const [autoVerifying, setAutoVerifying] = useState(Boolean(initialCode));

  // QR 스캔을 기본 동선으로 둔다(진입 즉시 카메라 시도). 코드 직접입력은 "코드 입력" 클릭 시 전환.
  // 카메라를 못 쓰는 환경(권한 거부 / 카메라 없음 / 인앱 브라우저 차단)은 useQrScanner 가
  // 실제 getUserMedia 결과로 판별해 available=false 폴백(카메라 박스 안에 코드 입력 안내)으로
  // 처리한다. UA 블랙리스트로 사전 배제하지 않는다 — 인앱이라도 카메라 되는 경우(iOS WKWebView
  // 등)의 QR 기회를 살리고, 안 되는 경우만 실측으로 폴백시킨다.
  const [scanning, setScanning] = useState(true);

  // handleVerify 는 react-query mutation 의존이라 매 렌더 identity 가 바뀐다. 의존성에 둔 채
  // 그대로 실행하면 effect 가 매 렌더 재실행돼 자동 검증이 무한 반복(React #185)된다.
  // 마운트 1회만 실행하도록 ref 로 가드한다.
  const didAutoVerifyRef = useRef(false);
  useEffect(() => {
    if (!initialCode || didAutoVerifyRef.current) return;
    didAutoVerifyRef.current = true;
    // 성공이면 로딩을 유지한 채 리다이렉트로 언마운트되게 둔다(폼이 깜빡이는 것 방지).
    // 실패면 폼 폴백으로 전환한다(handleVerify 가 실패 토스트를 직접 띄운다).
    void handleVerify(initialCode, 'link').then(ok => {
      if (!ok) setAutoVerifying(false);
    });
  }, [initialCode, handleVerify]);

  // 형식이 잘못된 코드로 딥링크 진입(자동 검증 미수행)한 경우, 마운트 시 1회 토스트로 안내하고
  // fail_code_verify(invalid_format) 이벤트를 발화한다(API 호출 없이 형식 무효 처리된 케이스).
  useEffect(() => {
    if (!hadInvalidCode) return;
    trackCodeLoginEvent('fail_code_verify', {
      login_method: 'link',
      reason: 'invalid_format',
      input_code: rawCode
    });
    // 마운트 동기 effect 에서 toast() 를 바로 부르면 첫 토스트가 유실된다 —
    // 부모(_app)의 Toaster 구독 effect 가 자식 effect 보다 늦게 실행되기 때문
    // (React: child effects run before parent). 다음 매크로태스크로 미뤄
    // Toaster 구독 이후에 띄운다. (typing/딥링크 검증 실패 토스트는 await 뒤라 무관)
    const toastId = setTimeout(() => {
      zusToast.error(t('codeLogin.entryFailed'), {
        position: 'top-right'
      });
    }, 0);
    return () => clearTimeout(toastId);
  }, [hadInvalidCode, rawCode, t]);
  if (autoVerifying) {
    return <FullScreenLoading label={t('codeLogin.loading')} />;
  }
  return <CenterLayout showLanguageSelector data-sentry-element="CenterLayout" data-sentry-component="CodeLoginPageView" data-sentry-source-file="CodeLoginPageView.tsx">
      <div className="flex w-full max-w-[450px] flex-col items-center gap-sm rounded-lg bg-default px-xl pb-lg pt-2xl shadow-xs">
        {/* Header: 로고 + 안내 문구 */}
        <div className="flex w-full flex-col items-center gap-sm pb-sm">
          <img src="/assets/ZEPQuiz-logo.svg" alt="ZEP QUIZ" className="h-10 w-auto" />
          <p className="text-center text-body-md-medium text-neutral">
            {t('codeLogin.guide')}
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-md">
          {/* 상단 슬롯: 스캔 전 = QR 스캔 버튼 / 스캔 중 = 카메라 영역 */}
          {scanning ? <QrScanner onScan={handleScan} /> : <Button type="button" variant="outlined" intent="neutral" size="lg" leftSlot={<QrScanIcon />} className="w-full" onClick={() => {
          setScanning(true);
          trackCodeLoginEvent('click_qr_scan_start', {
            login_method: 'scan'
          });
        }}>
              {t('codeLogin.scanQr')}
            </Button>}

          {/* "or" 구분선 */}
          <div className="flex w-full items-center justify-center gap-xs">
            <div className="h-px flex-1 bg-alternative" />
            <span className="text-body-sm-medium text-assistive">or</span>
            <div className="h-px flex-1 bg-alternative" />
          </div>

          {/* 하단 슬롯: 스캔 전 = 코드 입력 폼 / 스캔 중 = 코드 입력 전환 버튼 */}
          {scanning ? <Button type="button" variant="outlined" intent="neutral" size="lg" leftSlot={<CodeKeyIcon />} className="w-full" onClick={() => {
          setScanning(false);
          resetScanSession();
        }}>
              {t('codeLogin.enterCode')}
            </Button> : <CodeVerifyForm onVerify={code => handleVerify(code, 'typing')} isPending={isPending} defaultCode={rawCode} />}

          {/* 이메일 로그인 안내 */}
          <div className="flex items-center justify-center gap-2xs">
            <span className="text-body-sm-semibold text-neutral">
              {t('codeLogin.noCodeQuestion')}
            </span>
            <a href={emailLoginUrl} className="px-sm py-xs text-body-sm-semibold text-strong hover:underline" onClick={() => trackCodeLoginEvent('click_email_login_fallback', {})}>
              {t('codeLogin.emailLogin')}
            </a>
          </div>
        </div>
      </div>
    </CenterLayout>;
}